import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { OwnerInteractionDialog } from "../center/OwnerQuickDialogs";
import { dateTime, money, relativeDate, surfaceSx } from "../center/ownerCenterUi";
import OwnerClientCommitmentDialog from "./OwnerClientCommitmentDialog";
import OwnerCommitmentTimeDialog from "../center/OwnerCommitmentTimeDialog";
import { commitmentOperationalDetail, commitmentOperationalMeta, commitmentOperationalState, isCommitmentExecutable } from "../components/ownerCommitmentOperationalState";

const KIND_LABELS = {
  task: "Tarea",
  follow_up: "Seguimiento",
  appointment: "Reunión futura",
  promise: "Promesa",
  reminder: "Recordatorio",
  project_next_step: "Paso de proyecto",
};

const INTERACTION_LABELS = {
  conversation: "Conversación",
  message: "Mensaje",
  call: "Llamada",
  meeting: "Reunión realizada",
  note: "Nota",
};

const CHANNEL_LABELS = {
  whatsapp: "WhatsApp",
  phone: "Teléfono",
  email: "Email",
  instagram: "Instagram",
  in_person: "En persona",
  other: "Otro",
};

const PRIORITY_LABELS = { low: "Baja", normal: "Normal", high: "Alta" };
const STATUS_LABELS = { open: "Abierto", done: "Realizado", cancelled: "Cancelado" };

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function minutesLabel(value) {
  const minutes = Math.max(0, Number(value || 0));
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (!hours) return `${remainder} min`;
  return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
}

function timestamp(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : null;
}

function commitmentSort(row) {
  const now = Date.now();
  if (row.status === "open") {
    const executable = isCommitmentExecutable(row);
    const scheduled = timestamp(row.scheduledStartAt);
    const due = timestamp(row.dueAt);
    const review = timestamp(row.work?.reviewAt);
    const anchor = executable ? Math.min(scheduled || Infinity, due || Infinity) : Math.min(review || Infinity, due || Infinity, scheduled || Infinity);
    if (executable && anchor < now) return [0, anchor];
    if (executable) return [1, anchor];
    return [2, anchor];
  }
  return [4, -(timestamp(row.completedAt || row.updatedAt || row.createdAt) || 0)];
}

function sortedCommitments(rows = []) {
  return [...rows].sort((a, b) => {
    const aa = commitmentSort(a);
    const bb = commitmentSort(b);
    return aa[0] - bb[0] || aa[1] - bb[1];
  });
}

function firstFutureAppointment(rows = []) {
  const now = Date.now();
  return rows
    .filter((row) => row.status === "open" && isCommitmentExecutable(row) && row.kind === "appointment" && timestamp(row.scheduledStartAt) >= now)
    .sort((a, b) => timestamp(a.scheduledStartAt) - timestamp(b.scheduledStartAt))[0] || null;
}

function firstOpenCommitment(rows = []) {
  return sortedCommitments(rows).find((row) => row.status === "open" && isCommitmentExecutable(row)) || null;
}

function SummaryValue({ label, children, helper }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: "text.secondary", fontSize: ".65rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".055em" }}>{label}</Typography>
      <Typography sx={{ mt: .18, fontWeight: 1000, fontSize: ".88rem", lineHeight: 1.25 }}>{children}</Typography>
      {helper ? <Typography sx={{ mt: .15, color: "text.secondary", fontSize: ".66rem" }}>{helper}</Typography> : null}
    </Box>
  );
}

export default function OwnerClientActivityPanel({
  client,
  context,
  loading = false,
  error = "",
  platformProjects = [],
  onRefresh,
}) {
  const [commitmentOpen, setCommitmentOpen] = React.useState(false);
  const [editingCommitment, setEditingCommitment] = React.useState(null);
  const [timeCommitment, setTimeCommitment] = React.useState(null);
  const [interactionOpen, setInteractionOpen] = React.useState(false);
  const [actionError, setActionError] = React.useState("");
  const [savingStatusId, setSavingStatusId] = React.useState("");

  const commitments = context?.commitments || [];
  const interactions = context?.interactions || [];
  const initiatives = context?.initiatives || [];
  const recurringPlans = context?.recurringPlans || [];
  const sales = context?.sales || [];
  const totals = context?.totalsByCurrency || {};
  const recurringByCurrency = context?.recurringByCurrency || {};
  const timeSummary = context?.timeSummary || {};
  const rows = sortedCommitments(commitments);
  const openRows = rows.filter((row) => row.status === "open");
  const recentClosed = rows.filter((row) => row.status !== "open").slice(0, 5);
  const nextCommitment = firstOpenCommitment(commitments);
  const nextMeeting = firstFutureAppointment(commitments);
  const lastInteraction = interactions[0] || null;
  const now = Date.now();
  const overdueCount = openRows.filter((row) => {
    if (!isCommitmentExecutable(row)) return false;
    const dates = [timestamp(row.scheduledStartAt), timestamp(row.dueAt)].filter((value) => value !== null);
    return dates.length && Math.min(...dates) < now;
  }).length;

  const initiativeMap = new Map(initiatives.map((row) => [idOf(row), row]));
  const recurringMap = new Map(recurringPlans.map((row) => [idOf(row), row]));
  const saleMap = new Map(sales.map((row) => [idOf(row), row]));
  const projectMap = new Map(platformProjects.map((row) => [idOf(row), row]));

  if (!context && loading) {
    return <Box sx={{ ...surfaceSx, p: 2, minHeight: 120, display: "grid", placeItems: "center" }}><Stack spacing={1} alignItems="center"><CircularProgress size={24} /><Typography sx={{ color: "text.secondary", fontSize: ".78rem" }}>Cargando actividad, dinero y horas…</Typography></Stack></Box>;
  }
  if (!context && error) {
    return <Box sx={{ ...surfaceSx, p: 2 }}><Alert severity="error" action={<Button size="small" onClick={onRefresh} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button>}>{error}</Alert></Box>;
  }

  const changeStatus = async (commitment, status) => {
    setSavingStatusId(idOf(commitment));
    setActionError("");
    try {
      await ownerApi.updateCommitment(idOf(commitment), { status });
      await onRefresh?.();
    } catch (err) {
      setActionError(formatApiError(err, "No pude cambiar el estado de la actividad."));
    } finally {
      setSavingStatusId("");
    }
  };

  const openEditor = (commitment = null) => {
    setEditingCommitment(commitment);
    setCommitmentOpen(true);
  };

  const renderCommitment = (row) => {
    const initiative = initiativeMap.get(idOf(row.initiativeId));
    const recurring = recurringMap.get(idOf(row.recurringPlanId));
    const sale = saleMap.get(idOf(row.saleId));
    const project = projectMap.get(idOf(row.webProfitProjectId));
    const relevantDates = [timestamp(row.scheduledStartAt), timestamp(row.dueAt)].filter((value) => value !== null);
    const operationalState = commitmentOperationalState(row);
    const operationalMeta = commitmentOperationalMeta(row);
    const operationalDetail = commitmentOperationalDetail(row);
    const executable = isCommitmentExecutable(row);
    const isOverdue = row.status === "open" && executable && relevantDates.length > 0 && Math.min(...relevantDates) < now;
    const saving = savingStatusId === idOf(row);

    return (
      <Box key={idOf(row)} sx={{ py: 1.05, borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.065)", "&:last-of-type": { borderBottom: 0 } }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.1} alignItems={{ md: "flex-start" }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap alignItems="center">
              <Typography sx={{ fontWeight: 950, fontSize: ".84rem" }}>{row.title}</Typography>
              <Chip size="small" label={STATUS_LABELS[row.status] || row.status} variant="outlined" sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} />
              <Chip size="small" label={KIND_LABELS[row.kind] || row.kind} sx={{ height: 20, fontSize: ".62rem", fontWeight: 850 }} />
              {row.status === "open" && operationalState !== "ready" ? <Chip size="small" color={operationalState === "blocked" ? "warning" : operationalState === "waiting" ? "info" : "primary"} variant="outlined" label={operationalMeta.shortLabel} sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} /> : null}
              {isOverdue ? <Chip size="small" color="error" label="Vencido" sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} /> : null}
              <Chip size="small" color={row.priority === "high" ? "warning" : "default"} variant={row.priority === "high" ? "filled" : "outlined"} label={`Prioridad ${String(PRIORITY_LABELS[row.priority] || "Normal").toLowerCase()}`} sx={{ height: 20, fontSize: ".62rem", fontWeight: 850 }} />
            </Stack>
            <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap sx={{ mt: .55 }}>
              {row.scheduledStartAt && executable ? <Chip size="small" icon={<EventRoundedIcon />} label={`Agenda ${dateTime(row.scheduledStartAt)}`} sx={{ height: 21, fontSize: ".62rem" }} /> : null}
              {row.work?.reviewAt && !executable ? <Chip size="small" icon={<EventRoundedIcon />} label={`Revisar ${dateTime(row.work.reviewAt)}`} sx={{ height: 21, fontSize: ".62rem" }} /> : null}
              {row.dueAt ? <Chip size="small" label={`Límite ${relativeDate(row.dueAt)}`} sx={{ height: 21, fontSize: ".62rem" }} /> : null}
              {initiative ? <Chip size="small" label={`Proyecto · ${initiative.name}`} sx={{ height: 21, fontSize: ".62rem" }} /> : null}
              {recurring ? <Chip size="small" label={`Mensual · ${recurring.name}`} sx={{ height: 21, fontSize: ".62rem" }} /> : null}
              {sale ? <Chip size="small" label={`Venta · ${sale.title}`} sx={{ height: 21, fontSize: ".62rem" }} /> : null}
              {project ? <Chip size="small" label={`WP · ${project.name}`} sx={{ height: 21, fontSize: ".62rem" }} /> : row.webProfitProjectId ? <Chip size="small" label="WP · proyecto relacionado" sx={{ height: 21, fontSize: ".62rem" }} /> : null}
            </Stack>
            {operationalDetail && row.status === "open" ? <Typography sx={{ mt: .55, color: operationalState === "blocked" ? "warning.dark" : "text.secondary", fontSize: ".72rem", fontWeight: 900 }}>{operationalState === "waiting" ? `Esperando: ${operationalDetail}` : `Bloqueada por: ${operationalDetail}`}</Typography> : null}
            <Typography sx={{ mt: .55, color: "text.secondary", fontSize: ".7rem", fontWeight: 800 }}>
              {executable ? (row.estimatedMinutes ? `Estimado ${minutesLabel(row.estimatedMinutes)}` : "Sin estimar") : "No ejecutable ahora"} · real {minutesLabel(row.actualMinutes || 0)}
              {row.notes ? ` · ${row.notes}` : ""}
            </Typography>
          </Box>
          <Stack direction="row" spacing={.4} flexWrap="wrap" useFlexGap>
            <Button size="small" startIcon={<EditRoundedIcon />} disabled={saving} onClick={() => openEditor(row)} sx={{ textTransform: "none", fontWeight: 850 }}>Editar</Button>
            <Button size="small" startIcon={<AccessTimeRoundedIcon />} disabled={saving} onClick={() => setTimeCommitment(row)} sx={{ textTransform: "none", fontWeight: 850 }}>Tiempo</Button>
            {row.status === "open" ? (
              <>
                <Button size="small" color="success" startIcon={<DoneRoundedIcon />} disabled={saving} onClick={() => changeStatus(row, "done")} sx={{ textTransform: "none", fontWeight: 900 }}>Realizado</Button>
                <Button size="small" color="inherit" startIcon={<BlockRoundedIcon />} disabled={saving} onClick={() => changeStatus(row, "cancelled")} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
              </>
            ) : (
              <Button size="small" startIcon={<RestoreRoundedIcon />} disabled={saving} onClick={() => changeStatus(row, "open")} sx={{ textTransform: "none", fontWeight: 900 }}>Reabrir</Button>
            )}
          </Stack>
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ ...surfaceSx, p: { xs: 1.6, sm: 2 } }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2} alignItems={{ md: "flex-start" }}>
          <Box>
            <Typography sx={{ fontWeight: 1000 }}>Qué requiere atención</Typography>
            <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".75rem" }}>Próximos pasos, reuniones, contactos, dinero Owner y horas reales.</Typography>
          </Box>
          {loading ? <CircularProgress size={20} /> : null}
        </Stack>
        {error ? <Alert severity="error" sx={{ mt: 1.1 }} action={<Button size="small" onClick={onRefresh} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button>}>{error}</Alert> : null}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,minmax(0,1fr))" }, gap: 1.1, mt: 1.4 }}>
          <SummaryValue label="Próximo paso" helper={overdueCount ? `${overdueCount} vencido${overdueCount === 1 ? "" : "s"}` : ""}>{nextCommitment ? nextCommitment.title : "Sin próximo paso"}</SummaryValue>
          <SummaryValue label="Próxima reunión">{nextMeeting ? dateTime(nextMeeting.scheduledStartAt) : "Sin reunión agendada"}</SummaryValue>
          <SummaryValue label="Último contacto">{lastInteraction ? dateTime(lastInteraction.occurredAt) : "Sin contactos registrados"}</SummaryValue>
          <SummaryValue label="Horas">{Number(timeSummary.totalMinutes || 0) ? minutesLabel(timeSummary.totalMinutes) : "Sin horas registradas"}{Number(timeSummary.last30DaysMinutes || 0) ? ` · ${minutesLabel(timeSummary.last30DaysMinutes)} / 30d` : ""}</SummaryValue>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 1, mt: 1.2 }}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.025)" }}>
            <Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 900 }}>SALDO PENDIENTE</Typography>
            {Object.entries(totals).some(([, row]) => Number(row.outstanding || 0) > 0) ? Object.entries(totals).filter(([, row]) => Number(row.outstanding || 0) > 0).map(([currency, row]) => <Typography key={currency} sx={{ mt: .2, fontWeight: 950, fontSize: ".82rem" }}>{money(row.outstanding, currency)}</Typography>) : <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".78rem" }}>Sin saldo pendiente</Typography>}
          </Box>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.025)" }}>
            <Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 900 }}>MENSUALIDAD OWNER</Typography>
            {Object.keys(recurringByCurrency).length ? Object.entries(recurringByCurrency).map(([currency, amount]) => <Typography key={currency} sx={{ mt: .2, fontWeight: 950, fontSize: ".82rem" }}>{money(amount, currency)} / mes</Typography>) : <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".78rem" }}>Sin mensualidad Owner</Typography>}
          </Box>
        </Box>
      </Box>

      <Box sx={{ ...surfaceSx, p: { xs: 1.6, sm: 2 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}>
          <Box>
            <Typography sx={{ fontWeight: 1000 }}>Próximos pasos y reuniones</Typography>
            <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".75rem" }}>Una reunión futura es agenda; una reunión ocurrida se registra en Historial.</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddTaskRoundedIcon />} onClick={() => openEditor(null)} sx={{ textTransform: "none", fontWeight: 950 }}>Nueva actividad</Button>
        </Stack>
        {actionError ? <Alert severity="error" sx={{ mt: 1 }}>{actionError}</Alert> : null}
        <Divider sx={{ my: 1.2 }} />
        {openRows.length ? <Stack>{openRows.map(renderCommitment)}</Stack> : <Typography sx={{ py: 1.4, color: "text.secondary", fontSize: ".8rem" }}>Sin próximo paso. Creá una actividad para dejar explícito qué tiene que pasar después.</Typography>}
        {recentClosed.length ? (
          <>
            <Divider sx={{ my: 1.2 }} />
            <Typography sx={{ color: "text.secondary", fontSize: ".68rem", fontWeight: 900 }}>CERRADOS RECIENTES</Typography>
            <Stack>{recentClosed.map(renderCommitment)}</Stack>
          </>
        ) : null}
      </Box>

      <Box sx={{ ...surfaceSx, p: { xs: 1.6, sm: 2 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}>
          <Box>
            <Typography sx={{ fontWeight: 1000 }}>Historial</Typography>
            <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".75rem" }}>Conversaciones, llamadas, reuniones realizadas y anotaciones con fecha.</Typography>
          </Box>
          <Button variant="outlined" startIcon={<ForumRoundedIcon />} onClick={() => setInteractionOpen(true)} sx={{ textTransform: "none", fontWeight: 950 }}>Registrar contacto / nota</Button>
        </Stack>
        <Divider sx={{ my: 1.2 }} />
        {interactions.length ? (
          <Stack>
            {interactions.slice(0, 20).map((item) => (
              <Box key={idOf(item)} sx={{ py: .9, borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.065)", "&:last-of-type": { borderBottom: 0 } }}>
                <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap alignItems="center">
                  <Chip size="small" variant="outlined" label={INTERACTION_LABELS[item.kind] || "Contacto"} sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} />
                  <Typography sx={{ color: "text.secondary", fontSize: ".7rem", fontWeight: 750 }}>{CHANNEL_LABELS[item.channel] || "Otro"} · {dateTime(item.occurredAt)}</Typography>
                </Stack>
                <Typography sx={{ mt: .4, fontSize: ".84rem", fontWeight: 850, whiteSpace: "pre-wrap" }}>{item.summary}</Typography>
                {item.outcome ? <Typography sx={{ mt: .25, color: "text.secondary", fontSize: ".75rem", whiteSpace: "pre-wrap" }}>Quedó: {item.outcome}</Typography> : null}
              </Box>
            ))}
          </Stack>
        ) : <Typography sx={{ py: 1.4, color: "text.secondary", fontSize: ".8rem" }}>Todavía no registraste conversaciones, reuniones o notas con esta persona.</Typography>}
      </Box>

      <OwnerClientCommitmentDialog
        open={commitmentOpen}
        client={client}
        context={context}
        platformProjects={platformProjects}
        commitment={editingCommitment}
        onClose={() => { setCommitmentOpen(false); setEditingCommitment(null); }}
        onSaved={onRefresh}
      />
      <OwnerCommitmentTimeDialog open={Boolean(timeCommitment)} commitment={timeCommitment} onClose={() => setTimeCommitment(null)} onSaved={onRefresh} />
      <OwnerInteractionDialog open={interactionOpen} person={client} onClose={() => setInteractionOpen(false)} onSaved={onRefresh} />
    </>
  );
}
